import { useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import apiInstance from '@services/api/apiInstance'
import { useUserRoles, useAccessToken } from '@services/token/token.hooks'
import { leaseDate } from '@services/date/date.models'
import { investment as investmentAtom } from '@services/protocols/protocols.models'
import { useAtom } from 'jotai'
import { useSnackbar } from 'notistack'
import { useOfflineMutation } from '../../hooks/useOfflineMutation'

import {
  ProtocolStatuses,
  ProtocolTypes,
  CoveringCosts,
  KeyTypes,
  MeterTypes,
  PROTOCOL_TYPE,
  AnimalType,
  METER_TYPE
} from './protocols.consts'

export interface DateRange {
  startDate: Date
  endDate: Date
}

export interface Protocol {
  id: number
  leaseContract: number
  file: null
  signedAt: null
  acceptedAt: null
  status: ProtocolStatuses
  protocolType: ProtocolTypes
  person: null | number
  r4rRepresentative: null | number
  protocolWithTenant: true
  clientHasRepresentative: boolean
  clientRepresentativeAuthorizationDocument: null | string
  clientRepresentativeName: null | string
  clientRepresentativeSurname: null | string
  oneSidedProtocolReason: null
  generalComment: null | string
  clientSignature: null | string
  r4rRepSignature: null | string
  leaseStartDate: string
  effectiveLeaseEnd: string
  contractNumber: string
  numberOfLocators: null | number
  flat: null | string
  // New WIS contract fields
  isWisContract?: boolean
  tenantMaintenance?: boolean | null
  gardenMaintenanceGroundFloor?: boolean | null
  postHandoverDefects24H?: boolean | null
  tenantImprovements?: boolean | null
  masterKeyConsent?: boolean | null
}

export const useProtocolsList = () => {
  const { isTechnician } = useUserRoles()
  const { enqueueSnackbar } = useSnackbar()
  const [investment] = useAtom(investmentAtom)
  const [laseDate] = useAtom(leaseDate)

  const {
    data: acceptanceProtocolList = {
      count: -1,
      next: null,
      previous: null,
      results: []
    },
    isLoading: acceptanceProtocolLoading,
    ...acceptanceProtocolRest
  } = useQuery({
    queryKey: ['acceptanceProtocolList', investment, laseDate.startDate, laseDate.endDate],
    queryFn: () =>
      apiInstance<{
        count: number
        next: string | null
        previous: string | null
        results: Protocol[]
      }>({
        url: '/admin/protocol/protocols/',
        method: 'GET',
        params: {
          lease_contract__investment_fk: investment,
          lease_contract__lease_start__start_date: laseDate.startDate,
          lease_contract__lease_start__end_date: laseDate.endDate,
          protocol_type: 'Acceptance',
          limit: 1000
        }
      }).then(({ data }) => data),
    enabled: true && !!investment && !!laseDate && !isTechnician()
  })

  const {
    data: deliveryProtocolList = {
      count: -1,
      next: null,
      previous: null,
      results: []
    },
    isLoading: deliveryProtocolLoading,
    ...deliveryProtocolRest
  } = useQuery({
    queryKey: ['deliveryProtocolList', investment, laseDate.startDate, laseDate.endDate],
    queryFn: () =>
      apiInstance<{
        count: number
        next: string | null
        previous: string | null
        results: Protocol[]
      }>({
        url: '/admin/protocol/protocols/',
        method: 'GET',
        params: {
          lease_contract__investment_fk: investment,
          lease_contract__effective_lease_end__start_date: laseDate.startDate,
          lease_contract__effective_lease_end__end_date: laseDate.endDate,
          protocol_type: 'Delivery',
          limit: 1000
        }
      }).then(({ data }) => data),
    enabled: true && !!investment && !!laseDate && !isTechnician()
  })

  const protocolList = [...acceptanceProtocolList.results, ...deliveryProtocolList.results]

  const updateProtocol = useOfflineMutation<Protocol, any, Partial<Protocol>>({
    endpoint: (variables) => `/admin/protocol/protocols/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'protocol',
    protocolId: (variables) => variables.id as number,
    offlineMessage: 'Aktualizacja protokołu zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      //TODO: instead of refetching all the data, we should update the protocol in the list by setQueryData(queryKey, (oldData) => newData)
      deliveryProtocolRest.refetch()
      acceptanceProtocolRest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji protokołu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const signProtocol = useOfflineMutation<Protocol, any, Partial<Protocol> & { id: number }>({
    endpoint: (variables) => `/admin/protocol/protocols/${variables.id}/sign/`,
    method: 'POST',
    mutationType: 'sign',
    protocolId: (variables) => variables.id,
    offlineMessage: 'Podpisanie protokołu zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Protokół został podpisany`, {
        variant: 'success'
      })
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas podpisywania protokołu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const getProtocol = (protocolId: Protocol['id']) =>
    protocolList.find(({ id }) => id === protocolId)

  const getPreviousProtocol = (protocolId: Protocol['id']) => {
    const main = protocolList.find(({ id }) => id === protocolId)

    if (main) {
      return protocolList
        .filter(
          ({ id, protocolType }) => id !== protocolId && protocolType !== PROTOCOL_TYPE.delivery
        )
        .find(({ leaseContract }) => leaseContract === main.leaseContract)
    }

    return null
  }

  return {
    updateProtocol,
    protocolsList: protocolList,
    signProtocol,
    getProtocol,
    getPreviousProtocol,
    refetchProtocols: () => {
      deliveryProtocolRest.refetch()
      acceptanceProtocolRest.refetch()
    },
    ...deliveryProtocolRest,
    ...acceptanceProtocolRest,
    isLoading: acceptanceProtocolLoading || deliveryProtocolLoading
  }
}

export const getAcceptanceDeliveryProtocol = (id: any) => {
  const [investment] = useAtom(investmentAtom)
  const [laseDate] = useAtom(leaseDate)

  const {
    data: acceptanceProtocolList = {
      count: -1,
      next: null,
      previous: null,
      results: []
    },
    isLoading: acceptanceProtocolLoading,
    ...acceptanceProtocolRest
  } = useQuery({
    queryKey: ['acceptanceProtocolListData', id, investment, laseDate.startDate, laseDate.endDate],
    queryFn: () =>
      apiInstance<{
        count: number
        next: string | null
        previous: string | null
        results: Protocol[]
      }>({
        url: '/admin/protocol/protocols/',
        method: 'GET',
        params: {
          lease_contract_id: id,
          lease_contract__lease_start__start_date: laseDate.startDate,
          lease_contract__lease_start__end_date: laseDate.endDate,
          protocol_type: 'Acceptance',
          limit: 1000
        }
      }).then(({ data }) => data),
    enabled: true && !!investment && !!laseDate
  })

  const {
    data: deliveryProtocolList = {
      count: -1,
      next: null,
      previous: null,
      results: []
    },
    isLoading: deliveryProtocolLoading,
    ...deliveryProtocolRest
  } = useQuery({
    queryKey: ['deliveryProtocolData', id, investment, laseDate.startDate, laseDate.endDate],
    queryFn: () =>
      apiInstance<{
        count: number
        next: string | null
        previous: string | null
        results: Protocol[]
      }>({
        url: '/admin/protocol/protocols/',
        method: 'GET',
        params: {
          lease_contract_id: id,
          lease_contract__effective_lease_end__start_date: laseDate.startDate,
          lease_contract__effective_lease_end__end_date: laseDate.endDate,
          protocol_type: 'Delivery',
          limit: 1000
        }
      }).then(({ data }) => data),
    enabled: true && !!investment && !!laseDate
  })

  const acceptanceDeliveryList = [
    ...acceptanceProtocolList.results,
    ...deliveryProtocolList.results
  ]

  return {
    acceptanceDeliveryList,
    refetchProtocols: () => {
      deliveryProtocolRest.refetch()
      acceptanceProtocolRest.refetch()
    },
    ...deliveryProtocolRest,
    ...acceptanceProtocolRest,
    isLoading: acceptanceProtocolLoading || deliveryProtocolLoading
  }
}

export const useProcotol = (protocolId: Protocol['id']) => {
  const { isTechnician } = useUserRoles()

  const { data: protocol, ...rest } = useQuery({
    queryKey: ['protocol', protocolId],
    queryFn: () =>
      apiInstance<Protocol>({
        url: `/admin/protocol/protocols/${protocolId}/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId && !isTechnician()
  })

  return {
    protocol,
    refreshProtocol: rest.refetch,
    ...rest
  }
}

export const useProcotolFile = (protocolId: Protocol['id']) => {
  const { isTechnician } = useUserRoles()

  const { data: protocolFile, ...rest } = useQuery({
    queryKey: ['protocolFile', protocolId],
    queryFn: () =>
      apiInstance<Protocol>({
        url: `/admin/protocol/protocols/${protocolId}/file/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId && !isTechnician()
  })

  return {
    protocolFile,
    refreshProtocol: rest.refetch,
    ...rest
  }
}

export interface FurnitureType {
  id: number
  name: string
}

export const useFurnitureTypes = () => {
  const { data: furnitureTypes = [], ...rest } = useQuery({
    queryKey: ['furnitureTypes'],
    queryFn: () =>
      apiInstance<FurnitureType[]>({
        url: '/admin/protocol/furniture-types/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  return {
    furnitureTypes,
    ...rest
  }
}

export interface ControlQuestion {
  id: number
  roomType: {
    id: number
    name: string
    nameLocal: string
  }
  question: string
}

export const useInvestments = () => {
  const [investment, setInvestment] = useAtom(investmentAtom)
  const { data: investments = [], ...rest } = useQuery({
    queryKey: ['investments'],
    queryFn: () =>
      apiInstance<{ id: number; name: string; isHelpDeskOwner?: boolean }[]>({
        url: '/admin/protocol/investments/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  // Default only when unset or no longer present in the list — never on every refetch
  // (mobile SideMenuDrawer remounts SelectContent and would otherwise reset to first).
  // Prefer the investment the user is assigned to as help desk owner in the CRM.
  useEffect(() => {
    if (investments.length === 0) return
    const isValid = investment != null && investments.some(({ id }) => id === investment)
    if (!isValid) {
      const defaultInvestment =
        investments.find(({ isHelpDeskOwner }) => isHelpDeskOwner) ?? investments[0]
      setInvestment(defaultInvestment.id)
    }
  }, [investment, investments, setInvestment])

  return {
    setInvestment,
    investments,
    investment,
    ...rest
  }
}

export interface DebitNoteTitlePattern {
  id: number
  title: string
}

export const useControlQuestions = () => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: controlQuestions = [], ...rest } = useQuery({
    queryKey: ['controlQuestions'],
    queryFn: () =>
      apiInstance<ControlQuestion[]>({
        url: '/admin/protocol/control-questions/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const updateControlQuestion = useOfflineMutation({
    endpoint: (variables: Partial<ControlQuestion>) =>
      `/admin/protocol/control-questions/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'protocol',
    protocolId: (variables) => variables.id as number,
    offlineMessage: 'Aktualizacja pytania zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji pytania. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const { data: debitNoteTitlePattern = [] } = useQuery({
    queryKey: ['debitNoteTitlePattern'],
    queryFn: () =>
      apiInstance<DebitNoteTitlePattern[]>({
        url: '/admin/protocol/debit-note-title-pattern/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  return {
    updateControlQuestion,
    controlQuestions,
    debitNoteTitlePattern,
    ...rest
  }
}

export interface ItemType {
  id: number
  name: string
}

export const useItemTypes = () => {
  const { data: itemTypes = [], ...rest } = useQuery({
    queryKey: ['itemTypes'],
    queryFn: () =>
      apiInstance<ItemType[]>({
        url: '/admin/protocol/item-types/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  return {
    itemTypes,
    ...rest
  }
}

export interface ControlQuestionAnswer {
  id: number
  protocol: number
  room: number
  question: number
  scale: string
  comment: string
  image: string
  debitNotePosition: number
  coveringCost: CoveringCosts
  images: (File | string)[]
}

export const useControlQuestionAnswer = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: controlQuestionAnswer = [], ...rest } = useQuery({
    queryKey: ['controlQuestionAnswer', protocolId],
    queryFn: () =>
      apiInstance<ControlQuestionAnswer[]>({
        url: `/admin/protocol/protocols/${protocolId}/control-question-answer/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const updateControlQuestionAnswer = useOfflineMutation<
    ProtocolItem,
    any,
    {
      id: number
      scale: string
      comment: string
      debitNotePosition: number
      coveringCost: string
    }
  >({
    endpoint: (variables) =>
      `/admin/protocol/protocols/${protocolId}/control-question-answer/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'protocol',
    protocolId,
    offlineMessage: 'Aktualizacja odpowiedzi zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji odpowiedzi. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createControlQuestionAnswer = useOfflineMutation<
    ProtocolItem,
    any,
    {
      id: number
      scale: string
      comment: string
      debitNotePosition: number
      coveringCost: string
      room: number
    }
  >({
    endpoint: `/admin/protocol/protocols/${protocolId}/control-question-answer/`,
    method: 'POST',
    mutationType: 'protocol',
    protocolId,
    offlineMessage: 'Dodanie odpowiedzi zostanie wykonane po powrocie połączenia',
    transformToFormData: ({ id, ...answer }) => {
      const form = new FormData()

      if (id) {
        form.append('id', id.toString())
      }
      if (answer.scale) {
        form.append('scale', answer.scale)
      }
      if (answer.comment) {
        form.append('comment', answer.comment)
      }

      if (answer.debitNotePosition) {
        form.append('debit_note_position', answer.debitNotePosition.toString())
      }
      if (answer.coveringCost) {
        form.append('covering_cost', answer.coveringCost)
      }
      if (answer.room) {
        form.append('room', answer.room.toString())
      }
      if (id) {
        form.append('question', id.toString())
      }

      return form
    },
    onSuccess: () => {
      enqueueSnackbar(`Odpowiedź została dodana.`, {
        variant: 'success'
      })
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania odpowiedzi. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  return {
    controlQuestionAnswer,
    updateControlQuestionAnswer,
    createControlQuestionAnswer,
    ...rest
  }
}

export interface ProtocolFurniture {
  id: number
  furnitureType: FurnitureType['id']
  quantity: number
}

export const useProtocolFurniture = (protocolId?: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolFurniture = [], ...rest } = useQuery({
    queryKey: ['protocolFurniture', protocolId],
    queryFn: () =>
      apiInstance<ProtocolFurniture[]>({
        url: `/admin/protocol/protocols/${protocolId}/furniture/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const updateProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    Partial<ProtocolFurniture>
  >({
    endpoint: (variables) => `/admin/protocol/protocols/${protocolId}/furniture/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Aktualizacja mebla zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji mebli. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    Partial<ProtocolFurniture>
  >({
    endpoint: `/admin/protocol/protocols/${protocolId}/furniture/`,
    method: 'POST',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Dodanie mebla zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowy mebel`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania mebli. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolFurniture = useOfflineMutation<
    ProtocolFurniture,
    any,
    ProtocolFurniture['id']
  >({
    endpoint: (furnitureId) => `/admin/protocol/protocols/${protocolId}/furniture/${furnitureId}/`,
    method: 'DELETE',
    mutationType: 'furniture',
    protocolId,
    offlineMessage: 'Usunięcie mebla zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto mebel`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolFurniture,
    createProtocolFurniture,
    updateProtocolFurniture,
    deleteProtocolFurniture,
    ...rest
  }
}

export interface ProtocolItem {
  id: number
  itemType: ItemType['id']
  quantity: number
}

export const useProtocolItem = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolItem = [], ...rest } = useQuery({
    queryKey: ['protocolItem', protocolId],
    queryFn: () =>
      apiInstance<ProtocolItem[]>({
        url: `/admin/protocol/protocols/${protocolId}/item/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const updateProtocolItem = useOfflineMutation<ProtocolItem, any, Partial<ProtocolItem>>({
    endpoint: (variables: Partial<ProtocolItem>) =>
      `/admin/protocol/protocols/${protocolId}/item/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'item',
    protocolId,
    offlineMessage: 'Aktualizacja przedmiotu zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji przedmiotu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createProtocolItem = useOfflineMutation<ProtocolItem, any, Partial<ProtocolItem>>({
    endpoint: `/admin/protocol/protocols/${protocolId}/item/`,
    method: 'POST',
    mutationType: 'item',
    protocolId,
    offlineMessage: 'Dodanie przedmiotu zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowy przedmiot`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania przedmiotu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolItem = useOfflineMutation<ProtocolItem, any, ProtocolItem['id']>({
    endpoint: (itemId) => `/admin/protocol/protocols/${protocolId}/item/${itemId}/`,
    method: 'DELETE',
    mutationType: 'item',
    protocolId,
    offlineMessage: 'Usunięcie przedmiotu zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto przedmiot`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolItem,
    updateProtocolItem,
    createProtocolItem,
    deleteProtocolItem,
    ...rest
  }
}

export interface KeyType {
  id: number
  name: string
  isActive: boolean
  order: number
}

export interface KeyTypesResponse {
  count: number
  next: string | null
  previous: string | null
  results: KeyType[]
}

export interface ProtocolKey {
  id: number
  keyType: KeyTypes
  quantity: number
  comment: string | null
}

export const useKeyTypes = () => {
  const { accessToken } = useAccessToken()
  const { data: keyTypes = { results: [] }, ...rest } = useQuery<KeyTypesResponse>({
    queryKey: ['keyTypes'],
    queryFn: () =>
      apiInstance<KeyTypesResponse>({
        url: '/admin/protocol/key-types/',
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`
        }
      }).then(({ data }) => data),
    enabled: !!accessToken
  })

  return {
    keyTypes: keyTypes.results,
    ...rest
  }
}

export const useProtocolKey = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolKey = [], ...rest } = useQuery({
    queryKey: ['protocolKey', protocolId],
    queryFn: () =>
      apiInstance<ProtocolKey[]>({
        url: `/admin/protocol/protocols/${protocolId}/key/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const updateProtocolKey = useOfflineMutation<ProtocolKey, any, Partial<ProtocolKey>>({
    endpoint: (variables: Partial<ProtocolKey>) =>
      `/admin/protocol/protocols/${protocolId}/key/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'key',
    protocolId,
    offlineMessage: 'Aktualizacja klucza zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji klucza. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createProtocolKey = useOfflineMutation<ProtocolKey, any, Partial<ProtocolKey>>({
    endpoint: `/admin/protocol/protocols/${protocolId}/key/`,
    method: 'POST',
    mutationType: 'key',
    protocolId,
    offlineMessage: 'Dodanie klucza zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowy klucz`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania klucza. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolKey = useOfflineMutation<ProtocolKey, any, ProtocolKey['id']>({
    endpoint: (keyId) => `/admin/protocol/protocols/${protocolId}/key/${keyId}/`,
    method: 'DELETE',
    mutationType: 'key',
    protocolId,
    offlineMessage: 'Usunięcie klucza zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto klucz`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolKey,
    updateProtocolKey,
    createProtocolKey,
    deleteProtocolKey,
    ...rest
  }
}

export interface ProtocolMeter {
  id: number
  meterType: MeterTypes
  meterPhoto: string
  meterValue: number
  readingDate: string
}

export const useProtocolMeter = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolMeter = [], ...rest } = useQuery({
    queryKey: ['protocolMeter', protocolId],
    queryFn: () =>
      apiInstance<ProtocolMeter[]>({
        url: `/admin/protocol/protocols/${protocolId}/meter-reading/`,
        method: 'GET'
      }).then(({ data }) => {
        return data.filter((item) => (item.meterType as any) !== METER_TYPE.gas)
      }),
    enabled: true
  })

  const updateProtocolMeter = useOfflineMutation<
    ProtocolMeter,
    any,
    Partial<{
      id: number
      meterType: MeterTypes
      meterPhoto: File
      meterValue: number
      readingDate: string
    }>
  >({
    endpoint: (variables) =>
      `/admin/protocol/protocols/${protocolId}/meter-reading/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'meter',
    protocolId,
    offlineMessage: 'Aktualizacja licznika zostanie wykonana po powrocie połączenia',
    transformToFormData: (meter) => {
      const form = new FormData()
      if (meter.id) {
        form.append('id', meter.id.toString())
      }
      if (meter.meterPhoto) {
        form.append('meter_photo', meter.meterPhoto, meter.meterPhoto.name)
      }
      if (meter.meterType) {
        form.append('meter_type', meter.meterType)
      }
      if (meter.meterValue) {
        form.append('meter_value', meter.meterValue.toString())
      }
      if (meter.readingDate) {
        form.append('reading_date', meter.readingDate)
      }
      return form
    },
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji odczytu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  return {
    protocolMeter,
    updateProtocolMeter,
    ...rest
  }
}

export interface ProtocolRoom {
  id: number
  roomInstance: {
    id: number
    name: string
    nameLocal: string
  }
  video: string
  description: string
}

export const useProtocolRoom = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolRoom = [], ...rest } = useQuery({
    queryKey: ['protocolRoom', protocolId],
    queryFn: () =>
      apiInstance<ProtocolRoom[]>({
        url: `/admin/protocol/protocols/${protocolId}/room/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const updateProtocolRoom = useOfflineMutation<
    ProtocolRoom,
    any,
    Partial<ProtocolRoom & { video?: File }>
  >({
    endpoint: (variables) => `/admin/protocol/protocols/${protocolId}/room/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'room',
    protocolId,
    offlineMessage: 'Aktualizacja pokoju zostanie wykonana po powrocie połączenia',
    transformToFormData: (room) => {
      const roomForm = new FormData()
      if (room.id) {
        roomForm.append('id', room.id.toString())
      }
      if ((room as any).video) {
        roomForm.append('video', (room as any).video, (room as any).video.name)
      }
      roomForm.append('description', room.description ?? '')
      return roomForm
    },
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji pokoju. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  return {
    protocolRoom,
    updateProtocolRoom,
    ...rest
  }
}

export interface ProtocolRoomImage {
  id: number
  image: string
  comment?: string
}

export interface ProtocolRoomImageUpload {
  file: File
  comment?: string
}

export const useProtocolRoomImages = (protocolId: Protocol['id'], roomId: ProtocolRoom['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolRoomImages = [], ...rest } = useQuery({
    queryKey: ['protocolRoomImages', protocolId, roomId],
    gcTime: 15 * 60 * 1000,
    queryFn: () =>
      apiInstance<ProtocolRoomImage[]>({
        url: `/admin/protocol/protocols/${protocolId}/room/${roomId}/room-image/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && roomId !== -1
  })

  const updateProtocolRoomImages = useOfflineMutation<
    ProtocolRoomImage,
    any,
    Partial<ProtocolRoomImage>
  >({
    endpoint: (variables) =>
      `/admin/protocol/protocols/${protocolId}/room/${roomId}/room-image/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'room',
    protocolId,
    offlineMessage: 'Aktualizacja zdjęcia zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji zdjęcia. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createProtocolRoomImages = useOfflineMutation<
    ProtocolRoomImage,
    any,
    ProtocolRoomImageUpload
  >({
    endpoint: `/admin/protocol/protocols/${protocolId}/room/${roomId}/room-image/`,
    method: 'POST',
    mutationType: 'room',
    protocolId,
    offlineMessage: 'Dodanie zdjęcia zostanie wykonane po powrocie połączenia',
    transformToFormData: (data) => {
      const imageForm = new FormData()
      imageForm.append('image', data.file, data.file.name)
      if (data.comment) {
        imageForm.append('comment', data.comment)
      }
      return imageForm
    },
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowe zdjęcie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawaia zdjęcia. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolRoomImages = useOfflineMutation<ProtocolRoomImage, any, ProtocolRoom['id']>({
    endpoint: (keyId) =>
      `/admin/protocol/protocols/${protocolId}/room/${roomId}/room-image/${keyId}/`,
    method: 'DELETE',
    mutationType: 'room',
    protocolId,
    offlineMessage: 'Usunięcie zdjęcia zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto zdjęcie`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolRoomImages,
    updateProtocolRoomImages,
    createProtocolRoomImages,
    deleteProtocolRoomImages,
    ...rest
  }
}

export const useProtocolRoomImagesArray = (protocolId: Protocol['id'], rooms: any[]) => {
  const { data: protocolRoomImagesArray = [], ...rest } = useQuery({
    queryKey: ['protocolRoomImagesArray', protocolId, rooms],
    gcTime: 15 * 60 * 1000,
    queryFn: () => {
      const fetchImagesForRooms = rooms.map((room) =>
        apiInstance<ProtocolRoomImage[]>({
          url: `/admin/protocol/protocols/${protocolId}/room/${room.id}/room-image/`,
          method: 'GET'
        }).then(({ data }) => ({
          roomId: room.id,
          images: data
        }))
      )

      const results = Promise.all(fetchImagesForRooms)

      return results
    },
    enabled: true
  })

  return {
    protocolRoomImagesArray,
    ...rest
  }
}

export const useProtocolQuestionAnswerImages = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const createProtocolQuestionAnswerImages = useOfflineMutation<
    ProtocolRoomImage,
    any,
    { image: File; controlQuestionAnswerId: number }
  >({
    endpoint: (variables) =>
      `/admin/protocol/protocols/${protocolId}/control-question-answer/${variables.controlQuestionAnswerId}/control-question-answer-image/`,
    method: 'POST',
    mutationType: 'protocol',
    protocolId,
    offlineMessage: 'Dodanie zdjęcia zostanie wykonane po powrocie połączenia',
    transformToFormData: ({ image }) => {
      const imageForm = new FormData()
      imageForm.append('image', image, image.name)
      return imageForm
    },
    onSuccess: () => {},
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawaia zdjęcia. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolQuestionAnswerImages = useOfflineMutation<
    ProtocolRoomImage,
    any,
    { controlQuestionAnswerId: number; keyId: ProtocolRoom['id'] }
  >({
    endpoint: (variables) =>
      `/admin/protocol/protocols/${protocolId}/control-question-answer/${variables.controlQuestionAnswerId}/control-question-answer-image/${variables.keyId}/`,
    method: 'DELETE',
    mutationType: 'protocol',
    protocolId,
    offlineMessage: 'Usunięcie zdjęcia zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto zdjęcie`, {
        variant: 'success'
      })
    }
  })

  return {
    createProtocolQuestionAnswerImages,
    deleteProtocolQuestionAnswerImages
  }
}

function dataURItoBlob(dataURI: string) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1])
  else byteString = unescape(dataURI.split(',')[1])

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }

  return new Blob([ia], { type: mimeString })
}

export const useProtocolSign = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: locators = [], ...rest } = useQuery({
    queryKey: ['locators', protocolId],
    gcTime: 15 * 60 * 1000,
    queryFn: () =>
      apiInstance<
        {
          id: number
          firstName: string
          lastName: string
        }[]
      >({
        url: `/admin/protocol/protocols/${protocolId}/locators/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const signProtocol = useMutation({
    mutationFn: ({
      sign,
      r4rRepSignature,
      person,
      numberOfLocators,
      oneSidedProtocolReason,
      protocolWithTenant,
      clientHasRepresentative,
      clientRepresentativeName,
      clientRepresentativeSurname,
      clientRepresentativeAuthorizationDocument,
      tenantMaintenance,
      gardenMaintenanceGroundFloor,
      postHandoverDefects24H,
      tenantImprovements,
      masterKeyConsent
    }: {
      sign?: string
      r4rRepSignature: string
      person?: number
      numberOfLocators?: number
      oneSidedProtocolReason?: string
      protocolWithTenant: boolean
      clientHasRepresentative?: boolean
      clientRepresentativeName?: string
      clientRepresentativeSurname?: string
      clientRepresentativeAuthorizationDocument?: File
      tenantMaintenance?: boolean | null
      gardenMaintenanceGroundFloor?: boolean | null
      postHandoverDefects24H?: boolean | null
      tenantImprovements?: boolean | null
      masterKeyConsent?: boolean | null
    }) => {
      const signForm = new FormData()
      if (sign) {
        const blob = dataURItoBlob(sign)
        signForm.append('client_signature', blob, 'client_signature.png')
      }
      const blobR4R = dataURItoBlob(r4rRepSignature)
      signForm.append('r4r_rep_signature', blobR4R, 'r4r_rep_signature.png')
      if (person) {
        signForm.append('person', person.toString())
      }
      if (typeof numberOfLocators === 'number') {
        signForm.append('number_of_locators', numberOfLocators.toString())
      }
      if (oneSidedProtocolReason) {
        signForm.append('one_sided_protocol_reason', oneSidedProtocolReason)
      }
      if (typeof protocolWithTenant === 'boolean') {
        signForm.append('protocol_with_tenant', protocolWithTenant.toString())
      }
      if (clientHasRepresentative) {
        signForm.append('client_has_representative', clientHasRepresentative.toString())
      }
      if (clientRepresentativeName) {
        signForm.append('client_representative_name', clientRepresentativeName)
      }
      if (clientRepresentativeSurname) {
        signForm.append('client_representative_surname', clientRepresentativeSurname)
      }
      if (clientRepresentativeAuthorizationDocument) {
        signForm.append(
          'client_representative_authorization_document',
          clientRepresentativeAuthorizationDocument,
          clientRepresentativeAuthorizationDocument.name
        )
      }
      // Add WIS consent fields
      if (typeof tenantMaintenance === 'boolean') {
        signForm.append('tenant_maintenance', tenantMaintenance.toString())
      }
      if (typeof gardenMaintenanceGroundFloor === 'boolean') {
        signForm.append('garden_maintenance_ground_floor', gardenMaintenanceGroundFloor.toString())
      }
      if (typeof postHandoverDefects24H === 'boolean') {
        signForm.append('post_handover_defects_24h', postHandoverDefects24H.toString())
      }
      if (typeof tenantImprovements === 'boolean') {
        signForm.append('tenant_improvements', tenantImprovements.toString())
      }
      if (typeof masterKeyConsent === 'boolean') {
        signForm.append('master_key_consent', masterKeyConsent.toString())
      }
      return apiInstance<ProtocolRoom>({
        url: `/admin/protocol/protocols/${protocolId}/`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        transformRequest: (data) => data,
        data: signForm
      }).then(() =>
        apiInstance<ProtocolRoom>({
          url: `/admin/protocol/protocols/${protocolId}/sign/`,
          method: 'POST'
        })
      )
    },
    onSuccess: () => {
      enqueueSnackbar(`Protokół został podpisany`, {
        variant: 'success'
      })
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas podpisywania protokołu. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails:
          error?.response?.data && error.response?.status < 500
            ? JSON.stringify(error?.response?.data)
            : 'Nieoczekiwany błąd serwera (500)'
      })
    }
  })

  return {
    signProtocol,
    locators,
    ...rest
  }
}

export interface ProtocolAnimal {
  id: number
  animalType: AnimalType
  quantity: number
}

export const useProtocolAnimal = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolAnimal = [], ...rest } = useQuery({
    queryKey: ['protocolAnimal', protocolId],
    queryFn: () =>
      apiInstance<ProtocolAnimal[]>({
        url: `/admin/protocol/protocols/${protocolId}/animal/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const updateProtocolAnimal = useOfflineMutation<ProtocolAnimal, any, Partial<ProtocolAnimal>>({
    endpoint: (variables: Partial<ProtocolAnimal>) =>
      `/admin/protocol/protocols/${protocolId}/animal/${variables.id}/`,
    method: 'PATCH',
    mutationType: 'animal',
    protocolId,
    offlineMessage: 'Aktualizacja zwierzęcia zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji zwierząt. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const createProtocolAnimal = useOfflineMutation<ProtocolAnimal, any, Partial<ProtocolAnimal>>({
    endpoint: `/admin/protocol/protocols/${protocolId}/animal/`,
    method: 'POST',
    mutationType: 'animal',
    protocolId,
    offlineMessage: 'Dodanie zwierzęcia zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowe zwierzęta`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania zwierząt. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolAnimal = useOfflineMutation<ProtocolAnimal, any, ProtocolAnimal['id']>({
    endpoint: (animalId) => `/admin/protocol/protocols/${protocolId}/animal/${animalId}/`,
    method: 'DELETE',
    mutationType: 'animal',
    protocolId,
    offlineMessage: 'Usunięcie zwierzęcia zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto zwierzęta`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolAnimal,
    createProtocolAnimal,
    updateProtocolAnimal,
    deleteProtocolAnimal,
    ...rest
  }
}
